import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitMerge, Pencil, Trash2, Upload, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { deleteOrArchive } from "@/components/crm/CustomerActions";
import { CustomerImport, LastImport } from "@/components/crm/CustomerImport";
import { DuplicatesDialog } from "@/components/crm/CustomerMerge";
import { api, errorMessage } from "@/lib/api";
import { PageHead, ScopeFilters, btnGold, btnLine, inp, qs, usePeople } from "@/lib/crm";
import { fmtDate } from "@/pages/Dashboard";

const EMPTY = { name: "", company: "", email: "", phone: "", address: "", notes: "", rep_id: "" };

export function CustomerForm({ people, onDone, onClose }) {
  const [f, setF] = useState(EMPTY);
  const [dup, setDup] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (confirm = false) => {
    if (!f.name.trim()) return toast.error("Customer name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/customers", { ...f, rep_id: f.rep_id || null, confirm_duplicate: confirm });
      toast.success(`Customer ${data.name} created`);
      onDone(data);
    } catch (e) {
      if (e.response?.status === 409) setDup(errorMessage(e));
      else toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const fld = (k, label, type = "text") => (
    <label className="space-y-1 text-[11px] uppercase tracking-wider text-slate-500">{label}
      <input data-testid={`customer-${k}-input`} type={type} value={f[k]} onChange={(e) => { setF({ ...f, [k]: e.target.value }); setDup(""); }} className={inp} />
    </label>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="customer-dialog" className="max-w-xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">New customer</DialogTitle><DialogDescription>Customers belong to a showroom and a sales rep.</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {fld("name", "Name *")}{fld("company", "Company")}{fld("email", "Email", "email")}{fld("phone", "Phone")}
          <div className="sm:col-span-2">{fld("address", "Address")}</div>
          {people.me.role !== "rep" && (
            <label className="space-y-1 text-[11px] uppercase tracking-wider text-slate-500 sm:col-span-2">Sales rep
              <select data-testid="customer-rep-select" value={f.rep_id} onChange={(e) => setF({ ...f, rep_id: e.target.value })} className={inp}>
                <option value="">Me</option>
                {people.users.map((u) => <option key={u.id} value={u.id}>{u.name} · {people.showrooms.find((s) => s.id === u.showroom_id)?.name || "—"}</option>)}
              </select>
            </label>
          )}
          <label className="space-y-1 text-[11px] uppercase tracking-wider text-slate-500 sm:col-span-2">Notes
            <textarea data-testid="customer-notes-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={`${inp} h-20 py-2`} />
          </label>
        </div>
        {dup && (
          <div data-testid="customer-duplicate-warning" className="rounded-xl border border-amber-500/50 bg-amber-500/5 p-3 text-sm text-amber-700">
            {dup}
            <button data-testid="customer-create-anyway" onClick={() => save(true)} className="ml-2 font-semibold underline">Create anyway</button>
          </div>
        )}
        <button data-testid="customer-save-button" disabled={busy} onClick={() => save(false)} className={btnGold}>Create customer</button>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const nav = useNavigate();
  const people = usePeople();
  const [f, setF] = useState({});
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [importing, setImporting] = useState(false);
  const [dups, setDups] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => api.get(`/customers?${qs(f)}`).then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e))), 200);
    return () => clearTimeout(t);
  }, [f, tick]);
  const manage = people.me.role && people.me.role !== "rep";
  const refresh = () => setTick((n) => n + 1);
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  return (
    <div className="space-y-8" data-testid="customers-page">
      <PageHead kicker="CRM" title="Customers" sub="Every customer, their showroom, sales rep and projects."
        right={<div className="flex flex-wrap gap-2">
          {manage && <button data-testid="customers-duplicates-button" onClick={() => setDups(true)} className={btnLine}><GitMerge className="h-4 w-4" /> Duplicates</button>}
          {manage && <button data-testid="customers-import-button" onClick={() => setImporting(true)} className={btnLine}><Upload className="h-4 w-4" /> Import</button>}
          <button data-testid="new-customer-button" onClick={() => setOpen(true)} className={btnGold}><UserPlus className="h-4 w-4" /> New customer</button></div>} />
      {manage && <LastImport tick={tick} onUndone={refresh} />}
      {importing && <CustomerImport people={people} onClose={() => setImporting(false)} onDone={refresh} />}
      {dups && <DuplicatesDialog onClose={() => setDups(false)} />}
      <div className="flex flex-wrap items-center gap-3">
        <ScopeFilters people={people} f={f} setF={setF} prefix="customers" />
        <label className="inline-flex min-h-[44px] items-center gap-2 text-[15px] text-black">
          <Switch data-testid="customers-show-archived" checked={!!f.archived} onCheckedChange={(v) => setF({ ...f, archived: v ? "true" : "" })} /> Show archived
        </label>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-800">
        <table className="w-full min-w-[820px]" data-testid="customers-table">
          <thead><tr className="border-b border-slate-800 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {["Customer", "Contact", "Showroom", "Sales rep", "Projects", "Created", ""].map((h) => <th key={h} className="px-5 py-3">{h}</th>)}</tr></thead>
          <tbody>{rows?.map((c, i) => (
            <tr key={c.id} data-testid={`customer-row-${i}`} onClick={() => nav(`/customers/${c.id}`)} className="cursor-pointer border-b border-slate-800/60 text-sm transition-colors hover:bg-amber-500/5">
              <td className="px-5 py-3"><p className="font-semibold text-slate-100" data-testid={`customer-name-${i}`}>{c.name}{c.archived && <span className="ml-2 rounded-full bg-black/[0.06] px-2 py-0.5 text-[11px] font-semibold text-[#3C3C43]">Archived</span>}</p><p className="text-xs text-slate-500">{c.company}</p></td>
              <td className="px-5 py-3 text-xs text-slate-400">{c.email}<br />{c.phone}</td>
              <td className="px-5 py-3 text-slate-300">{c.showroom_name}</td>
              <td className="px-5 py-3 text-slate-300" data-testid={`customer-rep-${i}`}>{c.rep_name}</td>
              <td className="px-5 py-3 font-mono-num text-slate-300">{c.project_count}</td>
              <td className="px-5 py-3 text-slate-400">{fmtDate(c.created_at)}</td>
              <td className="px-2 py-1 text-right whitespace-nowrap">
                <button data-testid={`customer-row-edit-${i}`} title="Edit" aria-label="Edit customer" onClick={stop(() => nav(`/customers/${c.id}?edit=1`))}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#0062CC] hover:bg-[#007AFF]/10"><Pencil className="h-4 w-4" /></button>
                {manage && <button data-testid={`customer-row-delete-${i}`} title="Delete" aria-label="Delete customer" onClick={stop(() => deleteOrArchive(c, refresh, refresh))}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#D70015] hover:bg-[#FF3B30]/10"><Trash2 className="h-4 w-4" /></button>}
              </td>
            </tr>))}
          </tbody>
        </table>
        {rows?.length === 0 && <p data-testid="customers-empty" className="py-14 text-center text-sm text-slate-500">No customers yet.</p>}
      </div>
      {open && <CustomerForm people={people} onClose={() => setOpen(false)} onDone={(c) => nav(`/customers/${c.id}`)} />}
    </div>
  );
}
